import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useLexware } from '../../context/LexwareContext';

const { FiLock, FiShield, FiArrowRight, FiCheckCircle, FiEye, FiEyeOff, FiUsers, FiX, FiUpload, FiHardDrive, FiCloud, FiCheck, FiUser } = FiIcons;

const PLAIN_PASSWORD = "Weativ2021!";
const ADMIN_PASSWORD = "Sonnenschein2009!";

function LoginGate({ children }) {
  const { data, currentUser, setCurrentUser, restoreBackup, loading, cloudSyncStatus } = useLexware();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isMasterAuth, setIsMasterAuth] = useState(false);
  const [error, setError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [showAdminAuth, setShowAdminAuth] = useState(false);
  const [adminPasswordInput, setAdminPasswordInput] = useState('');
  const [adminError, setAdminError] = useState(false);
  const [pendingAdminUser, setPendingAdminUser] = useState(null);

  const backupInputRef = useRef(null);

  useEffect(() => {
    const sessionAuth = sessionStorage.getItem('grt_crm_auth');
    if (sessionAuth === 'true') {
      setIsMasterAuth(true);
    }
    setIsLoading(false);
  }, []);

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    if (password.trim() === PLAIN_PASSWORD) {
      setIsSuccess(true);
      setTimeout(() => {
        setIsMasterAuth(true);
        sessionStorage.setItem('grt_crm_auth', 'true');
      }, 800);
    } else {
      setError(true);
    }
  };

  const handleGuestLogin = () => {
    setCurrentUser({ id: 'guest', name: 'Gast-Nutzer', initials: 'GST' });
  };

  const handleUserSelect = async (user) => {
    if (user.initials === 'ADM') {
      setPendingAdminUser(user);
      setShowAdminAuth(true);
      setAdminPasswordInput('');
    } else {
      await setCurrentUser(user);
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    if (adminPasswordInput.trim() === ADMIN_PASSWORD) {
      await setCurrentUser(pendingAdminUser || { id: 'sys-adm', name: 'Administrator', initials: 'ADM' });
      setShowAdminAuth(false);
    } else {
      setAdminError(true);
    }
  };

  const handleBackupUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      const backupData = JSON.parse(text);
      const success = await restoreBackup(backupData);
      if (success) alert("Backup erfolgreich wiederhergestellt!");
    } catch (err) {
      alert("Ungültige Backup-Datei.");
    }
    e.target.value = null;
  };

  if (isLoading) return null;

  if (!isMasterAuth) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900 px-4">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-md p-10">
          <div className="flex justify-center mb-8">
            <div className={`w-20 h-20 rounded-3xl flex items-center justify-center shadow-xl transition-all ${isSuccess ? 'bg-green-500' : 'bg-blue-600'}`}>
              <SafeIcon icon={isSuccess ? FiCheckCircle : FiShield} className="text-white text-4xl" />
            </div>
          </div>
          <h2 className="text-3xl font-black text-center text-gray-900 mb-2">Sicherer Bereich</h2>
          <p className="text-center text-gray-500 mb-10 text-sm">Geben Sie das Master-Passwort ein.</p>
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="relative">
              <SafeIcon icon={FiLock} className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
              <input 
                type={showPassword ? "text" : "password"} 
                value={password} 
                onChange={e => setPassword(e.target.value)} 
                placeholder="Passwort" 
                className="w-full pl-14 pr-14 py-5 bg-gray-50 border-2 border-gray-100 rounded-2xl focus:outline-none focus:border-blue-500 text-xl font-medium text-center" 
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400">
                <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="text-xl" />
              </button>
            </div>
            {error && <div className="text-red-600 text-center text-sm font-bold bg-red-50 py-3 rounded-xl">Passwort nicht korrekt</div>}
            <button type="submit" className="w-full py-5 bg-gray-900 text-white rounded-2xl font-bold flex items-center justify-center gap-3 hover:bg-black transition-colors shadow-xl">
              System entsperren <SafeIcon icon={FiArrowRight} />
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  if (!currentUser) {
    const regularUsers = (data.users || []).filter(u => u.initials !== 'ADM');
    const isSystemEmpty = regularUsers.length === 0;

    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-100/80 backdrop-blur-xl px-4">
        <AnimatePresence mode="wait">
          {showAdminAuth ? (
            <motion.div key="admin-auth" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-sm p-8 relative">
              <button onClick={() => setShowAdminAuth(false)} className="absolute top-6 right-6 p-2 rounded-full hover:bg-gray-100 text-gray-400">
                <SafeIcon icon={FiX} />
              </button>
              <div className="flex flex-col items-center mb-6">
                <div className="w-16 h-16 rounded-2xl bg-gray-900 text-white flex items-center justify-center font-bold text-xl mb-4 shadow-lg">ADM</div>
                <h3 className="text-xl font-black text-gray-900">Administrator Login</h3>
              </div>
              <form onSubmit={handleAdminLogin} className="space-y-4">
                <input 
                  type="password" 
                  value={adminPasswordInput} 
                  onChange={e => setAdminPasswordInput(e.target.value)} 
                  placeholder="Admin-Passwort" 
                  className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-100 rounded-xl focus:outline-none focus:border-gray-900 text-center font-bold text-lg" 
                  autoFocus 
                />
                {adminError && <div className="text-red-500 text-center text-xs font-bold py-2">Falsches Passwort</div>}
                <button type="submit" className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-colors">Bestätigen</button>
              </form>
            </motion.div>
          ) : (
            <motion.div key="selection-screen" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -20, opacity: 0 }} className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl p-10">
              {isSystemEmpty ? (
                <div className="text-center py-6">
                  <div className="w-20 h-20 bg-orange-50 rounded-3xl flex items-center justify-center mx-auto mb-6 text-orange-500 border-2 border-orange-100">
                    <SafeIcon icon={FiHardDrive} className="text-4xl" />
                  </div>
                  <h2 className="text-2xl font-black text-gray-900 mb-2">System initialisieren</h2>
                  <p className="text-gray-500 text-sm max-w-sm mx-auto mb-10 leading-relaxed">
                    Es wurden keine Benutzerdaten gefunden. Spielen Sie ein Backup ein oder nutzen Sie den Gast-Modus.
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    <button onClick={() => backupInputRef.current.click()} className="p-6 bg-blue-50 border-2 border-blue-100 rounded-3xl hover:border-blue-500 hover:bg-blue-100 transition-all group flex flex-col items-center gap-3">
                      <div className="w-12 h-12 bg-blue-600 text-white rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <SafeIcon icon={FiUpload} className="text-xl" />
                      </div>
                      <span className="font-black text-blue-700 uppercase text-xs">Backup laden</span>
                    </button>
                    <button onClick={handleGuestLogin} className="p-6 bg-gray-50 border-2 border-transparent hover:border-gray-900 rounded-3xl transition-all group flex flex-col items-center gap-3">
                      <div className="w-12 h-12 bg-gray-900 text-white rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <SafeIcon icon={FiUser} className="text-xl" />
                      </div>
                      <span className="font-black text-gray-900 uppercase text-xs">Gast-Modus</span>
                    </button>
                  </div>
                  
                  <button onClick={() => handleUserSelect({ id: 'sys-adm', name: 'Administrator', initials: 'ADM' })} className="text-xs font-bold text-gray-400 hover:text-gray-600 uppercase tracking-widest">
                    Als Admin anmelden
                  </button>
                  <input type="file" ref={backupInputRef} className="hidden" accept=".json" onChange={handleBackupUpload} />
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-4 mb-8">
                    <div className="p-3 bg-blue-100 rounded-2xl text-blue-600">
                      <SafeIcon icon={FiUsers} className="text-3xl" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-black text-gray-900">Wer arbeitet gerade?</h2>
                      <p className="text-gray-500 text-sm font-medium">Wählen Sie Ihr Profil aus.</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[400px] overflow-y-auto p-2">
                    {regularUsers.map(user => (
                      <button key={user.id} onClick={() => handleUserSelect(user)} className="flex items-center gap-4 p-5 bg-gray-50 border-2 border-transparent hover:border-blue-500 hover:bg-blue-50 rounded-2xl transition-all group text-left">
                        <div className="w-12 h-12 rounded-xl shadow-sm flex items-center justify-center font-bold text-lg border border-gray-100 group-hover:scale-110 transition-transform bg-white text-blue-600">
                          {user.initials}
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">{user.name}</p>
                          <p className="text-[10px] text-gray-400 font-black uppercase">Kürzel: {user.initials}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                  <div className="mt-8 flex justify-between items-center px-2">
                    <button onClick={handleGuestLogin} className="text-[11px] text-gray-500 font-bold uppercase tracking-widest hover:text-gray-900">
                       Gast-Modus
                    </button>
                    <button onClick={() => handleUserSelect({ id: 'sys-adm', name: 'Administrator', initials: 'ADM' })} className="text-[11px] text-gray-400 font-bold uppercase tracking-widest hover:text-gray-600">
                      Admin
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  if (cloudSyncStatus === 'syncing') {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-100/80 backdrop-blur-xl">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-md p-10 text-center">
          <div className="w-20 h-20 bg-blue-100 rounded-3xl flex items-center justify-center mx-auto mb-6 relative">
            <SafeIcon icon={FiCloud} className="text-4xl text-blue-600" />
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }} className="absolute inset-0 border-4 border-blue-200 border-t-blue-600 rounded-3xl" />
          </div>
          <h3 className="text-2xl font-black text-gray-900 mb-2">Cloud-Synchronisation</h3>
          <p className="text-gray-500 text-sm">Daten werden geladen...</p>
        </motion.div>
      </div>
    );
  }

  return children;
}

export default LoginGate;